import { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  HelpCircle, Mail, Clock, AlertTriangle, Send, 
  MessageSquare, User, CheckCircle, Loader2, FileText
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import DashboardLayout from '../components/DashboardLayout';

const Support = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: 'General Inquiry',
    message: ''
  });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim() || !formData.email.trim() || !formData.message.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSending(true);
    
    // Simulate sending (in production, this would call an API endpoint)
    // The form data would be sent to matchstakesupport@gmail.com
    try {
      // Create mailto link as fallback
      const mailtoLink = `mailto:matchstakesupport@gmail.com?subject=${encodeURIComponent(`[MatchStake Support] ${formData.subject}`)}&body=${encodeURIComponent(`Name: ${formData.name}\nEmail: ${formData.email}\n\nMessage:\n${formData.message}`)}`;
      
      // Open mail client
      window.location.href = mailtoLink;
      
      setSent(true);
      toast.success('Opening your email client...');
      
      // Reset form after short delay
      setTimeout(() => {
        setFormData({ name: '', email: '', subject: 'General Inquiry', message: '' });
        setSent(false);
      }, 3000);
    } catch (error) {
      toast.error('Failed to open email client');
    } finally {
      setSending(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto" data-testid="support-page">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="w-16 h-16 rounded-2xl bg-[#00F0FF]/20 flex items-center justify-center mx-auto mb-4">
            <HelpCircle className="w-8 h-8 text-[#00F0FF]" />
          </div>
          <h1 className="font-heading text-3xl font-bold mb-2">MatchStake Support</h1>
          <p className="text-white/50">We're here to help with any questions or issues</p>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Contact Information */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            {/* Email Contact */}
            <div className="glass-card rounded-2xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-[#00F0FF]/20 flex items-center justify-center">
                  <Mail className="w-5 h-5 text-[#00F0FF]" />
                </div>
                <h2 className="font-heading font-semibold text-lg">Contact Support</h2>
              </div>
              
              <p className="text-white/70 mb-4">
                If you need help with your account, matches, deposits, withdrawals, or technical issues, please contact support:
              </p>
              
              <a 
                href="mailto:matchstakesupport@gmail.com"
                className="flex items-center gap-2 text-[#00F0FF] hover:underline font-mono text-lg mb-4"
                data-testid="support-email-link"
              >
                <Mail className="w-5 h-5" />
                matchstakesupport@gmail.com
              </a>
              
              <div className="flex items-center gap-2 text-white/50 text-sm">
                <Clock className="w-4 h-4" />
                <span>Support response time: 24–48 hours</span>
              </div>
            </div>

            {/* Match Disputes */}
            <div className="glass-card rounded-2xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-[#FFB800]/20 flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-[#FFB800]" />
                </div>
                <h2 className="font-heading font-semibold text-lg">Match Disputes</h2>
              </div>
              
              <p className="text-white/70 mb-4">
                If a match result was submitted incorrectly, please email support within 24 hours and include:
              </p>
              
              <ul className="space-y-2 mb-4">
                <li className="flex items-center gap-2 text-white/70">
                  <div className="w-1.5 h-1.5 rounded-full bg-[#FFB800]" />
                  Your username
                </li>
                <li className="flex items-center gap-2 text-white/70">
                  <div className="w-1.5 h-1.5 rounded-full bg-[#FFB800]" />
                  Match ID
                </li>
                <li className="flex items-center gap-2 text-white/70">
                  <div className="w-1.5 h-1.5 rounded-full bg-[#FFB800]" />
                  Screenshot proof of the match result
                </li>
              </ul>
              
              <div className="p-3 rounded-lg bg-[#FFB800]/10 border border-[#FFB800]/20">
                <p className="text-sm text-[#FFB800]">
                  Send dispute requests to: <span className="font-mono">matchstakesupport@gmail.com</span>
                </p>
              </div>
            </div>

            {/* Quick Links */}
            <div className="glass-card rounded-2xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-[#7000FF]/20 flex items-center justify-center">
                  <FileText className="w-5 h-5 text-[#7000FF]" />
                </div>
                <h2 className="font-heading font-semibold text-lg">Helpful Resources</h2>
              </div>
              
              <div className="space-y-2">
                <a href="/faq" className="block p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-colors">
                  <span className="text-white/70">Frequently Asked Questions</span>
                </a>
                <a href="/match-rules" className="block p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-colors">
                  <span className="text-white/70">Match Rules</span>
                </a>
                <a href="/withdrawal-policy" className="block p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-colors">
                  <span className="text-white/70">Withdrawal Policy</span>
                </a>
              </div>
            </div>
          </motion.div>

          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="glass-card rounded-2xl p-6"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-[#00FF94]/20 flex items-center justify-center">
                <MessageSquare className="w-5 h-5 text-[#00FF94]" />
              </div>
              <h2 className="font-heading font-semibold text-lg">Send a Message</h2>
            </div>

            {sent ? (
              <div className="text-center py-8">
                <CheckCircle className="w-16 h-16 text-[#00FF94] mx-auto mb-4" />
                <h3 className="font-semibold text-lg mb-2">Email Client Opened!</h3>
                <p className="text-white/50">Complete sending your message in your email app.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm text-white/50 mb-2">Name *</label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                    <Input
                      type="text"
                      placeholder="Your name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="input-dark pl-10"
                      data-testid="support-name-input"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm text-white/50 mb-2">Email *</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                    <Input
                      type="email"
                      placeholder="your@email.com"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="input-dark pl-10"
                      data-testid="support-email-input"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm text-white/50 mb-2">Subject</label>
                  <select
                    value={formData.subject}
                    onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                    className="w-full p-3 rounded-lg bg-white/5 border border-white/10 text-white focus:border-[#00F0FF] focus:outline-none"
                    data-testid="support-subject-select"
                  >
                    <option value="General Inquiry">General Inquiry</option>
                    <option value="Match Dispute">Match Dispute</option>
                    <option value="Account Issue">Account Issue</option>
                    <option value="Deposit/Withdrawal">Deposit/Withdrawal</option>
                    <option value="Technical Problem">Technical Problem</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm text-white/50 mb-2">Message *</label>
                  <textarea
                    placeholder="Describe your issue or question..."
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    rows={5}
                    className="w-full p-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder:text-white/30 focus:border-[#00F0FF] focus:outline-none resize-none"
                    data-testid="support-message-input"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={sending}
                  className="w-full btn-primary"
                  data-testid="support-submit-btn"
                >
                  {sending ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Opening Email...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Send Message
                    </>
                  )}
                </Button>

                <p className="text-xs text-white/40 text-center">
                  This will open your default email client with your message pre-filled.
                </p>
              </form>
            )}
          </motion.div>
        </div>

        {/* Bottom Notice */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="mt-8 p-4 rounded-xl bg-white/5 text-center"
        >
          <p className="text-white/50 text-sm">
            For urgent matters, please include "URGENT" in your email subject line. 
            We aim to respond to all inquiries within 24-48 hours.
          </p>
        </motion.div>
      </div>
    </DashboardLayout>
  );
};

export default Support;
